import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';

import './i18n';
import { AuthProvider, useAuth } from './context/AuthContext';

import Header from './components/Header';
import SubHeader from './components/SubHeader';
import LanguageSync from './components/LanguageSync';
import LandingPage from './pages/LandingPage';
import Callback from './pages/Callback';
import TranslatorDashboard from './pages/TranslatorDashboard';
import TranslationSetup from './pages/TranslationSetup';
import TranslationEditor from './pages/TranslationEditor';
import CreateTranslation from './pages/CreateTranslation';
import ProjectCreate from './pages/ProjectCreate';
import ImportTranslation from './pages/ImportTranslation';
import OAuthAuthorize from './pages/OAuthAuthorize';
import ProjectDetails from './pages/ProjectDetails';
import AdminDashboard from './pages/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import ClientApproval from './pages/ClientApproval';
import BarcodeTranslationView from './pages/BarcodeTranslationView';
import TranslationVerificationPage from './pages/TranslationVerificationPage';
import GoogleDocsTranslation from './pages/GoogleDocsTranslation';
import Settings from './pages/Settings';

import NotFound from './pages/NotFound';
import Unauthorized from './pages/Unauthorized';

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-white p-6 text-center border-t border-gray-200 text-gray-600 shadow-sm">
      <div className="mx-auto text-center">
        <p className="mb-2">&copy; {new Date().getFullYear()} {t('header.appName')}. {t('footer.allRightsReserved')}</p>
        <p className="text-sm">{t('footer.address')} | {t('footer.phone')} | {t('footer.email')}</p>
      </div>
    </footer>
  );
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { isGoogleAuthenticated, isGoogleLoading } = useAuth();

  useEffect(() => {
    if (!isGoogleLoading && !isGoogleAuthenticated) {
      sessionStorage.setItem(
        'post_login_redirect',
        `${location.pathname}${location.search}${location.hash}`
      );
    }
  }, [isGoogleAuthenticated, isGoogleLoading, location.hash, location.pathname, location.search]);

  if (isGoogleLoading) {
    return <div>Loading...</div>; 
  }

  return isGoogleAuthenticated ? children : <Navigate to="/" replace />;
};

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isApprovalPage = location.pathname.startsWith('/approve/');
  const isBarcodeViewPage = location.pathname.startsWith('/translation/view/');
  const isVerificationPage = location.pathname.startsWith('/verify/');
  const isTranslationEditorPage = location.pathname.startsWith('/translator/edit/');
  const isGoogleDocsPage = location.pathname.startsWith('/google-docs/translate');
  const isDashboardPage = location.pathname === '/translator/dashboard';

  if (isApprovalPage || isBarcodeViewPage || isVerificationPage || isGoogleDocsPage) {
    // Full-screen pages get layout without header/footer
    return <>{children}</>;
  }

  // Normal pages get header and footer
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {isAuthenticated && !isDashboardPage && <SubHeader />}
      <main className={`flex-1 w-full ${isTranslationEditorPage ? '' : 'mx-auto p-4'}`}>
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!clientId) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID is not set in environment variables');
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        Error: Google Client ID not configured. Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file.
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <AuthProvider>
        <Router>
          <LanguageSync />
          <AppLayout>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/callback" element={<Callback />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/oauth/authorize" element={<OAuthAuthorize />} />

              {/* Public approval route - no authentication required */}
              <Route path="/approve/:token" element={<ClientApproval />} />

              {/* Public barcode translation view route - no authentication required */}
              <Route path="/translation/view/:translationId" element={<BarcodeTranslationView />} />

              {/* Public translation verification route - no authentication required */}
              <Route path="/verify/:projectId" element={<TranslationVerificationPage />} />

              {/* Google Docs Translation - New workflow */}
              <Route path="/google-docs/translate" element={<GoogleDocsTranslation />} />

              {/* Settings Page */}
              <Route path="/settings" element={<Settings />} />

              <Route
                path="/translator/dashboard"
                element={
                  <ProtectedRoute>
                    <TranslatorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/translator/create"
                element={
                  <ProtectedRoute>
                    <CreateTranslation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/translator/project/create"
                element={
                  <ProtectedRoute>
                    <ProjectCreate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/translator/import"
                element={
                  <ProtectedRoute>
                    <ImportTranslation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/project/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectDetails />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/translator/setup/:translationId?"
                element={
                  <ProtectedRoute>
                    <TranslationSetup />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/translator/edit/:translationId"
                element={
                  <ProtectedRoute>
                    <TranslationEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
        <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
      </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
